import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RefreshCw, Package, Lock, Search, ArrowRight, User, UserCog,
  ShoppingCart, Trash2, Plus, History, Download, Home, Images, CheckCircle2, XCircle, ZoomIn, PhoneCall, Info,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { FABRICS, COLORS, SWATCHES, slug, stockStatus, unitOf, setFabricUnits, formatDate, formatDateTime, deriveFabricCodes, itemMatchesQuery } from "@/lib/fabricData";
import { AlteredLabel, AlteredOriginalLine, DeletedLabel } from "@/components/orderAlter";
import { getStock, placeOrder, getMyOrders, getColors, getFabrics, logView, formatApiError, fileUrl, getArrivals, coadminConfig } from "@/lib/api";
import useInactivityLogout from "@/hooks/useInactivityLogout";
import { ImageZoom } from "@/components/ImageZoom";
import { Disclaimer } from "@/components/Disclaimer";
import { CustomerNav } from "@/components/CustomerNav";
import { InstructionsBody } from "@/pages/Instructions";

const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
const CUSTOMER_KEY = "cincstock_customer";
const SEEN_REPLIES_KEY = "cincstock_seen_replies";

function loadSeenReplies() {
  try {
    return JSON.parse(localStorage.getItem(SEEN_REPLIES_KEY) || "{}");
  } catch {
    return {};
  }
}

function loadCustomer() {
  try {
    const raw = localStorage.getItem(CUSTOMER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function EntryGate({ onEnter }) {
  const [form, setForm] = useState({ client_name: "", company_name: "", phone: "" });
  const [coadminAvailable, setCoadminAvailable] = useState(false);

  useEffect(() => {
    coadminConfig().then((d) => setCoadminAvailable(!!d.available)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.company_name.trim() || !form.phone.trim())
      return toast.error("Please fill your name, company and phone");
    if (!/^\d{10}$/.test(form.phone.trim()))
      return toast.error("Phone must be exactly 10 digits");
    const customer = {
      client_name: form.client_name.trim(),
      company_name: form.company_name.trim(),
      phone: form.phone.trim(),
    };
    localStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer));
    onEnter(customer);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative">
      <div className="absolute top-5 right-5 z-10 flex items-center gap-2">
        {coadminAvailable && (
          <Link
            to="/co-admin"
            data-testid="gate-coadmin-login-link"
            className="inline-flex items-center gap-2 border border-white/20 bg-black/40 backdrop-blur px-3 py-2 text-xs uppercase tracking-wider text-white hover:bg-white hover:text-black transition-colors"
          >
            <UserCog className="h-3.5 w-3.5" /> Co-Admin
          </Link>
        )}
        <Link
          to="/admin"
          data-testid="gate-admin-login-link"
          className="inline-flex items-center gap-2 border border-white/20 bg-black/40 backdrop-blur px-3 py-2 text-xs uppercase tracking-wider text-white hover:bg-white hover:text-black transition-colors"
        >
          <Lock className="h-3.5 w-3.5" /> Admin Login
        </Link>
      </div>
      <div
        className="hidden lg:block bg-cover bg-center relative"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1746737198845-e1e74bea786e)" }}
      >
        <div className="absolute inset-0 bg-black/75" />
        <div className="absolute top-0 p-10">
          <span className="font-display font-black text-2xl tracking-tighter text-white">
            CiNCStock<span className="text-white/40">.in</span>
          </span>
        </div>
        <div className="absolute bottom-0 p-12">
          <h2 className="font-display font-black text-4xl tracking-tighter text-white leading-none">
            Live knitted-fabric stock, in kilograms.
          </h2>
          <p className="mt-3 text-white/50 max-w-sm text-sm">
            Enter your details once to browse availability and place orders in real time.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#0A0A0A]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="entry-gate-form">
          <div className="h-10 w-10 border border-white/20 flex items-center justify-center mb-6">
            <User className="h-5 w-5 text-white" />
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter text-white">Welcome</h1>
          <p className="text-white/40 text-sm mt-2 mb-8">
            Tell us who you are — we&apos;ll remember you on this device so you never have to enter it again.
          </p>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">Client Name</Label>
              <Input
                data-testid="gate-client-input"
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="Your name"
                className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">Company Name</Label>
              <Input
                data-testid="gate-company-input"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Company / firm"
                className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">Phone Number</Label>
              <Input
                data-testid="gate-phone-input"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                placeholder="10-digit mobile"
                className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="gate-enter-button"
            className="mt-8 w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold transition-colors"
          >
            Enter Stock Portal <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function StockPortal({ embeddedExit }) {
  const [customer, setCustomer] = useState(loadCustomer());
  const [stock, setStock] = useState([]);
  const [arrivals, setArrivals] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedFabric, setSelectedFabric] = useState(FABRICS[0].name);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [flashKey, setFlashKey] = useState(null);
  const [query, setQuery] = useState("");
  const prevMap = useRef({});

  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderMessage, setOrderMessage] = useState("");
  const [orderUrgent, setOrderUrgent] = useState(false);
  const [orderRequiredBy, setOrderRequiredBy] = useState("");
  const [reqMsg, setReqMsg] = useState("");
  const [reqSubmitting, setReqSubmitting] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [qty, setQty] = useState("");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [seenReplies, setSeenReplies] = useState(loadSeenReplies);
  const [guideOpen, setGuideOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [colorMap, setColorMap] = useState({});
  const [colorImgMap, setColorImgMap] = useState({});
  const [fabrics, setFabrics] = useState(FABRICS);
  const [submitResult, setSubmitResult] = useState(null);
  const [bannerZoom, setBannerZoom] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const mainRef = useRef(null);

  const swatch = (name) => colorMap[name] || SWATCHES[name] || "#333";
  const fabricImageOf = (name) => fabrics.find((f) => f.name === name)?.image_path || null;
  const colorImageOf = (name) => colorImgMap[name] || null;
  const stockImgMap = useMemo(() => {
    const m = {};
    stock.forEach((s) => { if (s.image_path) m[`${s.fabric}|${s.color}`] = s.image_path; });
    return m;
  }, [stock]);
  const imageFor = (fabric, color) => stockImgMap[`${fabric}|${color}`] || colorImgMap[color] || null;

  useEffect(() => {
    getColors().then((cs) => {
      const m = {};
      const im = {};
      cs.forEach((c) => { m[c.name] = c.hex; if (c.image_path) im[c.name] = c.image_path; });
      setColorMap(m);
      setColorImgMap(im);
    }).catch(() => {});
    getFabrics().then((fs) => {
      if (fs && fs.length) {
        setFabrics(fs);
        setFabricUnits(fs);
        setSelectedFabric((cur) => (fs.some((f) => f.name === cur) ? cur : fs[0].name));
      }
    }).catch(() => {});
  }, []);

  const selectFabric = (name) => {
    setQuery("");
    setSelectedFabric(name);
    if (customer) logView({ ...customer, type: "fabric", ref: name });
    if (typeof window !== "undefined" && window.innerWidth < 1024 && mainRef.current) {
      setTimeout(() => mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await getMyOrders(customer);
      setHistory(res.orders || []);
    } catch (e) {
      toast.error("Could not load your order history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const downloadHistoryCSV = () => {
    if (history.length === 0) return toast.error("No orders to download");
    const header = ["Order ID", "Date", "Order Status", "Fabric", "Colour", "Quantity", "Unit", "Item Status", "Order Total"];
    const rows = [header];
    history.forEach((o) => {
      (o.items || []).forEach((it) => {
        rows.push([
          o.id,
          o.created_at ? formatDateTime(o.created_at) : "",
          o.status || "Pending",
          it.fabric,
          it.color,
          it.quantity_kg,
          unitOf(it.fabric),
          o.status === "Cancelled" ? "Cancelled" : (it.status || "Pending"),
          o.total_kg,
        ]);
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `my-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Order history downloaded");
  };

  const load = async (silent = false) => {
    try {
      const data = await getStock();
      const map = {};
      data.forEach((i) => (map[`${i.fabric}|${i.color}`] = i.available_kg));
      if (silent) {
        Object.keys(map).forEach((k) => {
          if (prevMap.current[k] !== undefined && map[k] < prevMap.current[k]) {
            setFlashKey(k);
            setTimeout(() => setFlashKey(null), 1300);
          }
        });
      }
      prevMap.current = map;
      setStock(data);
      setLastSync(new Date());
      getArrivals().then((list) => {
        const am = {};
        (list || []).forEach((a) => { am[`${a.fabric}|${a.color}`] = a.arrival_date; });
        setArrivals(am);
      }).catch(() => {});
      // keep swatches & shared colour images current (e.g. after an admin rename)
      getColors().then((cs) => {
        const cm = {}; const im = {};
        cs.forEach((c) => { cm[c.name] = c.hex; if (c.image_path) im[c.name] = c.image_path; });
        setColorMap(cm);
        setColorImgMap(im);
      }).catch(() => {});
    } catch (e) {
      if (!silent) toast.error("Could not load stock");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!customer) return;
    load();
    const t = setInterval(() => load(true), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    if (customer && searchParams.get("history") === "1") {
      openHistory();
      const n = new URLSearchParams(searchParams);
      n.delete("history");
      setSearchParams(n, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, searchParams]);

  const cartByKey = useMemo(() => {
    const m = {};
    cart.forEach((i) => {
      const k = `${i.fabric}|${i.color}`;
      m[k] = (m[k] || 0) + i.quantity_kg;
    });
    return m;
  }, [cart]);

  const byFabric = useMemo(() => {
    const m = {};
    fabrics.forEach((f) => (m[f.name] = 0));
    stock.forEach((i) => {
      const reserved = cartByKey[`${i.fabric}|${i.color}`] || 0;
      m[i.fabric] = (m[i.fabric] || 0) + Math.max(0, i.available_kg - reserved);
    });
    return m;
  }, [stock, cartByKey]);

  const isSearching = query.trim().length > 0;
  const codeMap = useMemo(() => deriveFabricCodes(stock, fabrics), [stock, fabrics]);

  const rows = useMemo(() => {
    if (isSearching) {
      return stock.filter((i) => itemMatchesQuery(i, query, codeMap));
    }
    return stock.filter((i) => i.fabric === selectedFabric);
  }, [stock, selectedFabric, query, isSearching, codeMap]);
  const crossView = isSearching;

  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.quantity_kg, 0), [cart]);
  const cartUnit = useMemo(() => {
    if (cart.length === 0) return "kg";
    const units = new Set(cart.map((i) => unitOf(i.fabric)));
    return units.size === 1 ? [...units][0] : "items";
  }, [cart]);
  const qtyInCart = (fabric, color) =>
    cart.filter((i) => i.fabric === fabric && i.color === color).reduce((s, i) => s + i.quantity_kg, 0);

  const changeDetails = () => {
    localStorage.removeItem(CUSTOMER_KEY);
    setCustomer(null);
  };

  useInactivityLogout(!!customer, () => {
    localStorage.removeItem(CUSTOMER_KEY);
    setCustomer(null);
    setCart([]);
    toast.info("Signed out after 5 minutes of inactivity");
  }, 5);

  // Keep a logged-in customer on the Stock page when they press the browser Back
  // button: any open dialog is closed first, and they are never dropped back to
  // the login gate. Only an explicit Log Out returns them to the entry gate.
  const dialogStateRef = useRef({});
  dialogStateRef.current = { addOpen, cartOpen, historyOpen, submitResult };
  useEffect(() => {
    if (!customer) return;
    window.history.pushState({ cincstock: "portal" }, "");
    const onPop = () => {
      const d = dialogStateRef.current;
      if (d.addOpen || d.cartOpen || d.historyOpen || d.submitResult) {
        setAddOpen(false);
        setCartOpen(false);
        setHistoryOpen(false);
        setSubmitResult(null);
      }
      window.history.pushState({ cincstock: "portal" }, "");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const openAdd = (item) => {
    setActiveItem(item);
    setQty("");
    setReqMsg("");
    setAddOpen(true);
    if (customer) logView({ ...customer, type: "color", ref: item.fabric, color: item.color });
  };

  const addToCart = () => {
    const q = parseFloat(qty);
    if (!q || q <= 0) return toast.error(`Enter a valid quantity in ${unitOf(activeItem.fabric)}`);
    const already = qtyInCart(activeItem.fabric, activeItem.color);
    if (q + already > (activeItem.available_kg ?? 0))
      return toast.error("Please call 8010307363 / 7020006164 for the availability of the fabric.");

    setCart((prev) => {
      const idx = prev.findIndex((i) => i.fabric === activeItem.fabric && i.color === activeItem.color);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity_kg: next[idx].quantity_kg + q };
        return next;
      }
      return [...prev, { fabric: activeItem.fabric, color: activeItem.color, quantity_kg: q }];
    });
    toast.success(`Added ${fmt(q)} ${unitOf(activeItem.fabric)} · ${activeItem.color}`, { description: "In your order" });
    setAddOpen(false);
  };

  // When the desired quantity is not available, the customer can still send a
  // message/request to the admin directly from the add-to-order screen.
  const sendRequestToAdmin = async () => {
    const q = parseFloat(qty);
    if (!q || q <= 0) return toast.error(`Enter the quantity you need in ${unitOf(activeItem.fabric)}`);
    setReqSubmitting(true);
    try {
      const res = await placeOrder({
        client_name: customer.client_name,
        company_name: customer.company_name,
        phone: customer.phone,
        items: [{ fabric: activeItem.fabric, color: activeItem.color, quantity_kg: q }],
        message: reqMsg.trim(),
        urgent: false,
        required_by: null,
        is_stock_request: true,
      });
      setReqMsg("");
      setQty("");
      setAddOpen(false);
      setSubmitResult(res.order);
      load(true);
      toast.success("Request sent to admin", { description: "We'll get back to you soon" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setReqSubmitting(false);
    }
  };

  const removeFromCart = (fabric, color) =>
    setCart((prev) => prev.filter((i) => !(i.fabric === fabric && i.color === color)));

  const updateCartQty = (fabric, color, value) => {
    const q = parseFloat(value);
    setCart((prev) =>
      prev.map((i) =>
        i.fabric === fabric && i.color === color ? { ...i, quantity_kg: isNaN(q) ? 0 : q } : i
      )
    );
  };

  const submitOrder = async () => {
    if (cart.length === 0) return toast.error("Your order is empty");
    if (cart.some((i) => i.quantity_kg <= 0)) return toast.error("Every item needs a quantity greater than 0");
    setSubmitting(true);
    try {
      const res = await placeOrder({
        client_name: customer.client_name,
        company_name: customer.company_name,
        phone: customer.phone,
        items: cart.map((i) => ({ fabric: i.fabric, color: i.color, quantity_kg: i.quantity_kg })),
        message: orderMessage.trim(),
        urgent: orderUrgent,
        required_by: orderRequiredBy || null,
      });
      setCart([]);
      setCartOpen(false);
      setOrderMessage("");
      setOrderUrgent(false);
      setOrderRequiredBy("");
      setSubmitResult(res.order);
      load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      load(true);
    } finally {
      setSubmitting(false);
    }
  };

  const enterPortal = (c) => {
    setCustomer(c);
    // On successful login the customer lands on the Instructions page first.
    navigate("/instructions");
  };

  if (!customer) return <EntryGate onEnter={enterPortal} />;

  const codeOf = (name) => fabrics.find((f) => f.name === name)?.code || "";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <CustomerNav
        onHistory={openHistory}
        rightExtra={
          <Button
            onClick={() => setCartOpen(true)}
            data-testid="open-cart-button"
            className="relative rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider font-semibold transition-colors"
          >
            <ShoppingCart className="h-3.5 w-3.5 sm:mr-2" />
            <span className="hidden sm:inline">Order</span>
            {cart.length > 0 && (
              <span
                data-testid="cart-count"
                className="ml-1.5 sm:ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1 bg-black text-white text-[10px] tabular"
              >
                {cart.length}
              </span>
            )}
          </Button>
        }
      />

      <Disclaimer />

      {/* Hero strip */}
      <div className="border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          <p className="text-[11px] uppercase tracking-[0.3em] text-white/40 mb-3">
            Welcome back, {customer.client_name}
          </p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-white max-w-3xl">
            Build your order.
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/50">
            <span className="tabular">{fabrics.length} fabrics</span>
            <span className="tabular">{COLORS.length} colours each</span>
            <span className="flex items-center gap-2">
              <RefreshCw className="h-3.5 w-3.5" />
              {lastSync ? `Synced ${lastSync.toLocaleTimeString()}` : "Syncing…"}
            </span>
          </div>
        </div>
      </div>

      {/* Body: sidebar + table */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
          {/* Sidebar */}
          <aside className="lg:col-span-4 xl:col-span-3 bg-[#0A0A0A] p-4 sm:p-5">
            <div className="relative mb-4" data-testid="color-search-wrap">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input
                data-testid="color-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search fabric, code or colour…"
                className="rounded-none h-11 bg-[#141414] border-white/10 pl-10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
              />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-4">Fabric Type</p>
            <div className="flex flex-col gap-px bg-white/10 border border-white/10 max-h-[70vh] overflow-y-auto thin-scroll">
              {fabrics.map((f) => {
                const active = f.name === selectedFabric;
                return (
                  <div
                    key={f.name}
                    className={`flex items-stretch ${active ? "bg-white" : "bg-[#0A0A0A] hover:bg-white/[0.06]"} transition-colors duration-200`}
                  >
                    <button
                      data-testid={`fabric-tab-${slug(f.name)}`}
                      onClick={() => selectFabric(f.name)}
                      className={`flex-1 flex items-center gap-3 px-4 py-3 text-left min-w-0 ${active ? "text-black" : "text-white"}`}
                    >
                      <span
                        className={`h-8 w-8 shrink-0 border flex items-center justify-center overflow-hidden ${active ? "border-black/20 bg-black/5" : "border-white/15 bg-[#141414]"}`}
                        data-testid={`fabric-tab-thumb-${slug(f.name)}`}
                      >
                        {fabricImageOf(f.name) ? (
                          <img src={fileUrl(fabricImageOf(f.name))} alt={f.name} className="h-full w-full object-cover" />
                        ) : (
                          <Images className={`h-4 w-4 ${active ? "text-black/25" : "text-white/20"}`} />
                        )}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium truncate">{f.name}</span>
                        {f.code && (
                          <span className={`block text-[10px] uppercase tracking-wider ${active ? "text-black/50" : "text-white/35"}`}>
                            {f.code}
                          </span>
                        )}
                      </span>
                    </button>
                    <Link
                      to={`/catalogs?fabric=${encodeURIComponent(f.name)}`}
                      data-testid={`fabric-view-catalog-${slug(f.name)}`}
                      title={`View ${f.name} catalog`}
                      className={`shrink-0 flex items-center gap-1 px-3 border-l text-[10px] uppercase tracking-wider transition-colors ${
                        active ? "border-black/15 text-black/70 hover:bg-black hover:text-white" : "border-white/10 text-white/50 hover:bg-white hover:text-black"
                      }`}
                    >
                      <Images className="h-3.5 w-3.5" />
                      <span className="hidden xl:inline">Catalog</span>
                    </Link>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Main table */}
          <main ref={mainRef} className="lg:col-span-8 xl:col-span-9 bg-[#0A0A0A] p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
              <div className="text-left">
                {isSearching ? (
                  <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-white">Search results</h2>
                ) : (
                  <div className="flex items-center gap-3">
                    <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-white">
                      {selectedFabric}
                    </h2>
                    {codeOf(selectedFabric) && (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-black bg-white px-2 py-0.5">
                        {codeOf(selectedFabric)}
                      </span>
                    )}
                  </div>
                )}
                <p className="text-sm text-white/40">{isSearching ? `${rows.length} match(es) across all fabrics` : "Choose a colour and enter the quantity you need."}</p>
              </div>
            </div>

            {/* Fabric type banner */}
            {!isSearching && (
              <div className="mb-6 border border-white/10 bg-[#141414] overflow-hidden" data-testid="fabric-banner">
                {fabricImageOf(selectedFabric) ? (
                  <button
                    onClick={() => setBannerZoom(true)}
                    data-testid="fabric-banner-zoom"
                    className="relative block w-full aspect-[16/6] bg-[#0F0F0F] cursor-zoom-in group"
                  >
                    <img
                      src={fileUrl(fabricImageOf(selectedFabric))}
                      alt={selectedFabric}
                      className="h-full w-full object-cover"
                      data-testid="fabric-banner-img"
                    />
                    <span className="absolute bottom-3 right-3 h-9 w-9 flex items-center justify-center bg-black/60 border border-white/20 text-white opacity-80 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="h-4 w-4" />
                    </span>
                  </button>
                ) : (
                  <div className="aspect-[16/6] w-full bg-[#0F0F0F] flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2 text-white/25" data-testid="fabric-banner-placeholder">
                      <Images className="h-8 w-8" />
                      <span className="text-[10px] uppercase tracking-[0.2em]">No image yet</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* header row */}
            <div className="grid grid-cols-12 gap-4 px-2 pb-3 border-b border-white/10 text-xs font-bold uppercase tracking-[0.2em] text-white/40">
              <div className="col-span-8">{crossView ? "Fabric · Colour" : "Colour"}</div>
              <div className="col-span-4 text-right">Add to order</div>
            </div>

            <div className="divide-y divide-white/10">
              {loading && <div className="py-16 text-center text-white/40 text-sm">Loading…</div>}
              {!loading && rows.length === 0 && (
                <div className="py-16 text-center text-white/40 text-sm">No colours match your search.</div>
              )}
              {rows.map((item, idx) => {
                const key = `${item.fabric}|${item.color}`;
                const inCart = qtyInCart(item.fabric, item.color);
                const tid = `${slug(item.fabric)}-${slug(item.color)}`;
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: Math.min(idx * 0.015, 0.25) }}
                    className="grid grid-cols-12 gap-4 items-center px-2 py-4 hover:bg-white/[0.02] transition-colors"
                    data-testid={`stock-row-${tid}`}
                  >
                    <div className="col-span-8 flex items-center gap-3 min-w-0">
                      {crossView && (
                        <span className="h-9 w-9 border border-white/15 bg-[#141414] shrink-0 flex items-center justify-center overflow-hidden" data-testid={`fabric-thumb-${tid}`}>
                          {fabricImageOf(item.fabric) ? (
                            <img src={fileUrl(fabricImageOf(item.fabric))} alt={item.fabric} className="h-full w-full object-cover" />
                          ) : (
                            <Images className="h-4 w-4 text-white/20" />
                          )}
                        </span>
                      )}
                      {imageFor(item.fabric, item.color) ? (
                        <button
                          onClick={() => setZoomImg(fileUrl(imageFor(item.fabric, item.color)))}
                          data-testid={`color-thumb-${tid}`}
                          title={`View ${item.color}`}
                          className="h-9 w-9 border border-white/15 bg-[#141414] shrink-0 overflow-hidden cursor-zoom-in hover:border-white/40 transition-colors"
                        >
                          <img src={fileUrl(imageFor(item.fabric, item.color))} alt={item.color} className="h-full w-full object-cover" />
                        </button>
                      ) : (
                        <span className="h-9 w-9 border border-white/15 bg-[#141414] shrink-0 flex items-center justify-center" data-testid={`color-thumb-${tid}`} title="No image">
                          <Images className="h-4 w-4 text-white/15" />
                        </span>
                      )}
                      <span
                        className="h-7 w-7 border border-white/20 shrink-0"
                        style={{ backgroundColor: swatch(item.color) }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{item.color}</p>
                        {crossView && <p className="text-[10px] uppercase tracking-wider text-white/40">{item.fabric}</p>}
                        {inCart > 0 && (
                          <p className="text-[10px] uppercase tracking-wider text-emerald-400">
                            {fmt(inCart)} {unitOf(item.fabric)} in order
                          </p>
                        )}
                        {arrivals[key] ? (
                          <p className="text-[10px] uppercase tracking-wider text-amber-400" data-testid={`arrival-note-${tid}`}>
                            Arriving ~ {formatDate(arrivals[key])}
                          </p>
                        ) : (item.available_kg ?? 0) <= (item.min_kg ?? 0) ? (
                          <p className="text-[10px] uppercase tracking-wider text-red-400 font-bold" data-testid={`unavailable-badge-${tid}`}>
                            Currently Unavailable
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="col-span-4 flex justify-end">
                      <Button
                        onClick={() => openAdd(item)}
                        data-testid={`add-button-${tid}`}
                        className="rounded-none bg-transparent border border-white/20 text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </main>
        </div>
      </div>

      {/* Add-item dialog (centered) */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent
          className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md rounded-none flex flex-col max-h-[90dvh]"
          data-testid="add-item-sheet"
        >
          {activeItem && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl tracking-tight text-white">
                  Add to order
                </DialogTitle>
                <DialogDescription className="text-white/50">
                  {activeItem.fabric}
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto -mx-1 px-1">
              <div className="mt-2 flex items-center gap-3 p-4 border border-white/10 bg-[#141414]">
                <span
                  className="h-10 w-10 border border-white/20"
                  style={{ backgroundColor: swatch(activeItem.color) }}
                />
                <div>
                  <p className="text-sm text-white">{activeItem.color}</p>
                  <p className="text-xs text-white/40">Enter the quantity you'd like to order</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-white/50">Quantity ({unitOf(activeItem.fabric)})</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  autoFocus
                  data-testid="add-qty-input"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addToCart()}
                  placeholder="e.g. 5"
                  className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
                />
                {parseFloat(qty) > 0 &&
                  parseFloat(qty) + qtyInCart(activeItem.fabric, activeItem.color) > (activeItem.available_kg ?? 0) && (
                    <div className="mt-3 space-y-3" data-testid="unavailable-request-block">
                      <div
                        data-testid="low-stock-warning"
                        className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-300"
                      >
                        <PhoneCall className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                          This quantity isn't available right now.
                          {arrivals[`${activeItem.fabric}|${activeItem.color}`] && (
                            <span data-testid="add-arrival-note" className="block mt-1 text-amber-200">Approx arrival: {formatDate(arrivals[`${activeItem.fabric}|${activeItem.color}`])}</span>
                          )}
                          <span className="block mt-1">Call 8010307363 / 7020006164, or send a message to our team below and we'll get back to you.</span>
                        </span>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Message to admin (optional)</Label>
                        <textarea
                          value={reqMsg}
                          maxLength={499}
                          rows={3}
                          onChange={(e) => setReqMsg(e.target.value.slice(0, 499))}
                          data-testid="request-message-input"
                          placeholder={`e.g. I need ${qty || "…"} ${unitOf(activeItem.fabric)} of ${activeItem.color}. When can you arrange it?`}
                          className="w-full rounded-none bg-[#141414] border border-white/10 text-white text-sm p-2.5 placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 resize-none"
                        />
                        <p className="text-[10px] text-white/30 text-right tabular">{reqMsg.length}/499</p>
                      </div>
                      <Button
                        onClick={sendRequestToAdmin}
                        disabled={reqSubmitting}
                        data-testid="send-request-button"
                        className="w-full rounded-none bg-amber-500 text-black hover:bg-amber-400 text-sm uppercase tracking-wider font-semibold transition-colors disabled:opacity-50"
                      >
                        {reqSubmitting ? "Sending…" : "Send Request to Admin"}
                      </Button>
                    </div>
                  )}
              </div>
              </div>

              {!(parseFloat(qty) > 0 &&
                parseFloat(qty) + qtyInCart(activeItem.fabric, activeItem.color) > (activeItem.available_kg ?? 0)) && (
                <Button
                  onClick={addToCart}
                  data-testid="confirm-add-button"
                  className="mt-4 shrink-0 w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold transition-colors"
                >
                  Add to Order <Plus className="h-4 w-4 ml-2" />
                </Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart / order dialog (centered) */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent
          className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-lg rounded-none flex flex-col max-h-[90dvh]"
          data-testid="cart-sheet"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight text-white">Your Order</DialogTitle>
            <DialogDescription className="text-white/50">
              Review your fabrics, then submit. The admin will receive and execute your order.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 flex-1 overflow-y-auto thin-scroll -mx-1 px-1">
            {cart.length === 0 ? (
              <div className="py-16 text-center text-white/40 text-sm" data-testid="cart-empty">
                No fabrics added yet. Tap <span className="text-white">Add</span> on any colour.
              </div>
            ) : (
              <div className="space-y-px bg-white/10 border border-white/10">
                {cart.map((i) => {
                  const tid = `${slug(i.fabric)}-${slug(i.color)}`;
                  return (
                    <div key={`${i.fabric}|${i.color}`} className="bg-[#0A0A0A] p-3 flex items-center gap-3" data-testid={`cart-item-${tid}`}>
                      {imageFor(i.fabric, i.color) ? (
                        <button
                          onClick={() => setZoomImg(fileUrl(imageFor(i.fabric, i.color)))}
                          data-testid={`cart-color-thumb-${tid}`}
                          title={`View ${i.color}`}
                          className="h-9 w-9 border border-white/20 shrink-0 overflow-hidden cursor-zoom-in hover:border-white/50 transition-colors"
                        >
                          <img src={fileUrl(imageFor(i.fabric, i.color))} alt={i.color} className="h-full w-full object-cover" />
                        </button>
                      ) : (
                        <span className="h-9 w-9 border border-white/20 shrink-0" style={{ backgroundColor: swatch(i.color) }} />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{i.color}</p>
                        <p className="text-[11px] text-white/40 truncate">{i.fabric}</p>
                      </div>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={i.quantity_kg}
                        onChange={(e) => updateCartQty(i.fabric, i.color, e.target.value)}
                        data-testid={`cart-qty-${tid}`}
                        className="w-20 h-9 rounded-none bg-[#141414] border-white/10 text-white text-right tabular focus-visible:ring-2 focus-visible:ring-white/30"
                      />
                      <span className="text-xs text-white/40">{unitOf(i.fabric)}</span>
                      <button
                        onClick={() => removeFromCart(i.fabric, i.color)}
                        data-testid={`cart-remove-${tid}`}
                        className="text-white/40 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-4 border-t border-white/10 pt-4 mb-4 space-y-3" data-testid="order-extra-fields">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="button"
                  onClick={() => setOrderUrgent((v) => !v)}
                  data-testid="order-urgent-toggle"
                  className={`flex items-center gap-2 px-3 py-2 border text-xs uppercase tracking-wider transition-colors ${
                    orderUrgent ? "border-red-500 bg-red-500/15 text-red-300" : "border-white/15 bg-[#0F0F0F] text-white/60 hover:border-white/40"
                  }`}
                >
                  <span className={`h-3.5 w-3.5 border flex items-center justify-center ${orderUrgent ? "bg-red-500 border-red-500" : "border-white/40"}`}>
                    {orderUrgent && <CheckCircle2 className="h-3 w-3 text-white" />}
                  </span>
                  Mark Urgent
                </button>
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Required by (optional)</Label>
                  <Input
                    type="date"
                    value={orderRequiredBy}
                    onChange={(e) => setOrderRequiredBy(e.target.value)}
                    data-testid="order-required-by"
                    className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Message to admin (optional)</Label>
                <textarea
                  value={orderMessage}
                  maxLength={499}
                  rows={2}
                  onChange={(e) => setOrderMessage(e.target.value.slice(0, 499))}
                  data-testid="order-message-input"
                  placeholder="Any note about this order for the CiNCStock team…"
                  className="w-full rounded-none bg-[#141414] border border-white/10 text-white text-sm p-2.5 placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 resize-none"
                />
                <p className="text-[10px] text-white/30 text-right tabular">{orderMessage.length}/499</p>
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-white/10 pt-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-white/50">Total</span>
              <span className="tabular text-xl font-display font-black text-white" data-testid="cart-total">
                {fmt(cartTotal)} {cartUnit}
              </span>
            </div>
            <div className="p-3 border border-white/10 bg-[#0F0F0F] mb-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Order by</p>
              <p className="text-sm text-white">{customer.client_name}</p>
              <p className="text-xs text-white/50">{customer.company_name} · {customer.phone}</p>
            </div>
            <Button
              onClick={submitOrder}
              disabled={submitting || cart.length === 0}
              data-testid="submit-order-button"
              className="w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold transition-colors disabled:opacity-40"
            >
              {submitting ? "Submitting…" : (<>Submit Order <ArrowRight className="h-4 w-4 ml-2" /></>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order history dialog (centered) */}
      <Dialog open={historyOpen} onOpenChange={(open) => {
        setHistoryOpen(open);
        if (!open) {
          // Mark all current replies as seen so the "New reply" highlight clears next time.
          const map = {};
          history.forEach((o) => { if (o.admin_reply) map[o.id] = o.admin_reply; });
          setSeenReplies(map);
          try { localStorage.setItem(SEEN_REPLIES_KEY, JSON.stringify(map)); } catch {}
        }
      }}>
        <DialogContent
          className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-lg rounded-none flex flex-col max-h-[85vh]"
          data-testid="history-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight text-white">Your Order History</DialogTitle>
            <DialogDescription className="text-white/50">
              Orders placed by {customer.client_name} ({customer.company_name}) in the last 72 hours.
            </DialogDescription>
          </DialogHeader>

          <p data-testid="history-disclaimer" className="text-[11px] leading-snug text-white/40 border-y border-white/10 py-2 -mx-1 px-1">
            The stock mentioned can vary with the actual quantity available, please call or WhatsApp 8010307363 / 7020006164 for more information.
          </p>

          <div className="mt-2 flex-1 overflow-y-auto thin-scroll -mx-1 px-1 space-y-3">
            {historyLoading && <div className="py-12 text-center text-white/40 text-sm">Loading…</div>}
            {!historyLoading && history.length === 0 && (
              <div className="py-12 text-center text-white/40 text-sm" data-testid="history-empty">
                No orders in the last 72 hours.
              </div>
            )}
            {!historyLoading && history.map((o) => {
              const hasNewReply = o.admin_reply && seenReplies[o.id] !== o.admin_reply;
              return (
              <div key={o.id} className={`border ${hasNewReply ? "border-emerald-500/50 ring-1 ring-inset ring-emerald-500/30" : "border-white/10"}`} data-testid={`history-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-white/10 bg-[#141414]">
                  <span className="text-xs text-white/50 tabular">
                    {o.created_at ? formatDateTime(o.created_at) : "—"}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {hasNewReply && (
                      <span data-testid={`history-newreply-${o.id}`} className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-emerald-500 bg-emerald-500/20 text-emerald-300 font-bold animate-pulse">
                        New reply
                      </span>
                    )}
                    {o.urgent && (
                      <span data-testid={`history-urgent-${o.id}`} className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-red-500 bg-red-500/15 text-red-300 font-bold">
                        Urgent
                      </span>
                    )}
                    {o.required_by && (
                      <span data-testid={`history-requiredby-${o.id}`} className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-amber-500/50 text-amber-300">
                        By {formatDate(o.required_by)}
                      </span>
                    )}
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
                      o.status === "Cancelled" || o.status === "Rejected" ? "border-red-500/40 text-red-400"
                      : o.status === "Approved" ? "border-emerald-500/40 text-emerald-400"
                      : o.status === "Hold" ? "border-orange-500/40 text-orange-400"
                      : "border-yellow-500/40 text-yellow-400"}`}>
                      {o.status || "Pending"}
                    </span>
                  </div>
                </div>
                <div className="divide-y divide-white/10">
                  {(o.items || []).map((it, i) => {
                    const rejected = it.status === "Rejected" || o.status === "Cancelled" || !!it.deleted_by;
                    return (
                      <div key={i} className="flex items-center gap-3 px-4 py-2">
                        <span className="w-5 shrink-0 text-right tabular text-[11px] text-white/40" data-testid={`history-item-serial-${o.id}-${i}`}>{i + 1}.</span>
                        {imageFor(it.fabric, it.color) ? (
                          <button
                            onClick={() => setZoomImg(fileUrl(imageFor(it.fabric, it.color)))}
                            data-testid={`history-color-thumb-${slug(it.fabric)}-${slug(it.color)}`}
                            title={`View ${it.color}`}
                            className="h-6 w-6 border border-white/20 shrink-0 overflow-hidden cursor-zoom-in hover:border-white/50 transition-colors"
                          >
                            <img src={fileUrl(imageFor(it.fabric, it.color))} alt={it.color} className="h-full w-full object-cover" />
                          </button>
                        ) : (
                          <span className="h-4 w-4 border border-white/20 shrink-0" style={{ backgroundColor: swatch(it.color) }} />
                        )}
                        <span className="text-xs flex-1 min-w-0 flex flex-col">
                          <AlteredOriginalLine item={it} testid={`history-item-original-${o.id}-${i}`} />
                          <span className={`flex items-center min-w-0 ${rejected ? "text-white/40 line-through" : "text-white"}`}>
                            <span className="truncate">{it.fabric} · {it.color}</span>
                            <AlteredLabel by={it.altered_by} testid={`history-item-altered-label-${o.id}-${i}`} />
                            <DeletedLabel by={it.deleted_by} testid={`history-item-deleted-label-${o.id}-${i}`} />
                          </span>
                        </span>
                        {it.status && it.status !== "Pending" && o.status !== "Cancelled" && (
                          <span className={`text-[9px] uppercase tracking-wider ${it.status === "Accepted" ? "text-emerald-400" : "text-red-400"}`}>
                            {it.status}
                          </span>
                        )}
                        {it.delivered_kg !== null && it.delivered_kg !== undefined ? (
                          <div className="flex flex-col items-end shrink-0" data-testid={`history-qty-${o.id}-${i}`}>
                            <span className="tabular text-[11px] text-white/50">Ordered: {fmt(it.quantity_kg)} {unitOf(it.fabric)}</span>
                            <span className="tabular text-xs text-emerald-400 font-medium" data-testid={`history-delivered-${o.id}-${i}`}>Delivered: {fmt(it.delivered_kg)} {unitOf(it.fabric)}</span>
                          </div>
                        ) : (
                          <span className="tabular text-xs text-white">{fmt(it.quantity_kg)} {unitOf(it.fabric)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {(o.status === "Approved" || o.admin_reply) && (
                  <div className="px-4 py-3 border-t border-white/10 space-y-2" data-testid={`history-reply-${o.id}`}>
                    {o.status === "Approved" && (
                      <div className="flex items-center gap-2 text-xs text-emerald-400" data-testid={`history-accepted-note-${o.id}`}>
                        <CheckCircle2 className="h-4 w-4 shrink-0" /> Order accepted. Thanks.
                      </div>
                    )}
                    {o.admin_reply && (
                      <div className={`border p-2.5 ${hasNewReply ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/10 bg-[#0F0F0F]"}`}>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Message from CiNCStock</p>
                        <p className="text-sm text-white/90 break-words whitespace-pre-wrap">{o.admin_reply}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
            })}
          </div>

          <div className="mt-4 border-t border-white/10 pt-4">
            <Button
              onClick={downloadHistoryCSV}
              disabled={history.length === 0}
              data-testid="download-history-button"
              className="w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold transition-colors disabled:opacity-40"
            >
              <Download className="h-4 w-4 mr-2" /> Download (Excel)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submit result dialog: availability per item */}
      <Dialog open={!!submitResult} onOpenChange={(o) => !o && setSubmitResult(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md rounded-none" data-testid="submit-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight text-white">Order submitted</DialogTitle>
            <DialogDescription className="text-white/50">
              The admin has received your order and will approve or decline it. Track it under History.
            </DialogDescription>
          </DialogHeader>
          {submitResult && (
            <div className="mt-2 space-y-px bg-white/10 border border-white/10">
              {(submitResult.items || []).map((it, i) => (
                <div key={i} className="bg-[#0A0A0A] p-3 flex items-center gap-3" data-testid={`result-item-${i}`}>
                  <span className="h-8 w-8 border border-white/20 shrink-0" style={{ backgroundColor: swatch(it.color) }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{it.color}</p>
                    <p className="text-[11px] text-white/40 truncate">{it.fabric} · {fmt(it.quantity_kg)} {unitOf(it.fabric)}</p>
                  </div>
                  {it.available ? (
                    <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold" data-testid={`result-available-${i}`}>
                      <CheckCircle2 className="h-4 w-4" /> Available
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-400 text-xs font-semibold" data-testid={`result-unavailable-${i}`}>
                      <XCircle className="h-4 w-4" /> Not Available
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <Button
            onClick={() => setSubmitResult(null)}
            data-testid="result-close-button"
            className="mt-6 w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold transition-colors"
          >
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {/* Fabric banner zoom lightbox */}
      <ImageZoom
        src={fabricImageOf(selectedFabric) ? fileUrl(fabricImageOf(selectedFabric)) : null}
        alt={selectedFabric}
        open={bannerZoom}
        onOpenChange={setBannerZoom}
      />

      {/* Colour image zoom lightbox */}
      <ImageZoom
        src={zoomImg}
        alt="Colour"
        open={!!zoomImg}
        onOpenChange={(o) => !o && setZoomImg(null)}
      />

      {/* First-visit guide */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-2xl rounded-none flex flex-col max-h-[88vh]" data-testid="first-visit-guide">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight text-white flex items-center gap-2">
              <Info className="h-5 w-5 text-white/60" /> CiNCStock कैसे चलाएं / How to Use
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 flex-1 overflow-y-auto thin-scroll -mx-1 px-1">
            <InstructionsBody />
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={() => setGuideOpen(false)} data-testid="guide-got-it" className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider transition-colors">
              ठीक है / Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
